import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2, ArrowRight, Mail } from "lucide-react";
import { API } from "../lib/api";

const LABELS = {
  mobicars_1user_monthly: "1 utilisateur — 69€/mois",
  mobicars_5users_monthly: "Forfait 5 utilisateurs — 329€/mois",
  mobicars_license_onetime: "Licence complète — 469€",
};

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sid = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [info, setInfo] = useState({});

  useEffect(() => {
    if (!sid) {
      setStatus("error");
      return;
    }
    let tries = 0;
    let stopped = false;
    const poll = async () => {
      try {
        const r = await fetch(`${API}/payments/status/${sid}`);
        const j = await r.json();
        setInfo(j);
        if (j.payment_status === "paid") {
          setStatus("paid");
          return;
        }
      } catch (e) {
        console.error("status poll failed", e);
      }
      tries += 1;
      if (tries >= 12) {
        setStatus("pending");
        return;
      }
      if (!stopped) setTimeout(poll, 2000);
    };
    poll();
    return () => {
      stopped = true;
    };
  }, [sid]);

  return (
    <div className="min-h-screen bg-[#09090b] text-white font-['Manrope'] flex items-center justify-center px-6">
      <div className="max-w-lg w-full text-center border border-zinc-800 bg-zinc-900/50 rounded-lg p-10" data-testid="checkout-success">
        {status === "checking" && (
          <>
            <Loader2 size={52} className="mx-auto text-indigo-400 mb-5 animate-spin" />
            <h1 className="font-['Outfit'] font-black text-3xl tracking-tight mb-2">Validation du paiement…</h1>
            <p className="text-zinc-400">Merci de patienter quelques secondes.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle2 size={56} className="mx-auto text-emerald-400 mb-5" strokeWidth={1.5} />
            <h1 className="font-['Outfit'] font-black text-3xl tracking-tight mb-3" data-testid="success-title">
              Paiement confirmé
            </h1>
            {info.lookup_key && (
              <p className="text-indigo-300 font-semibold mb-4 font-['JetBrains_Mono'] text-sm">
                {LABELS[info.lookup_key] || info.lookup_key}
              </p>
            )}
            <div className="flex items-start gap-3 text-left bg-zinc-950/60 border border-zinc-800 rounded-md p-4 mb-6">
              <Mail size={20} className="text-indigo-400 shrink-0 mt-0.5" />
              <p className="text-zinc-300 text-sm">
                Votre compte <strong>MobicarsVSD</strong> a été créé automatiquement. Un email
                {info.email ? ` a été envoyé à ${info.email}` : " vous a été envoyé"} avec un lien pour créer votre mot de passe.
              </p>
            </div>
            <button
              data-testid="go-to-app"
              onClick={() => navigate("/login")}
              className="inline-flex items-center gap-2 bg-white text-zinc-950 font-bold px-6 py-3 rounded-md hover:bg-zinc-200 transition-colors"
            >
              Accéder à MobicarsVSD <ArrowRight size={18} strokeWidth={2.5} />
            </button>
          </>
        )}
        {status === "pending" && (
          <>
            <Loader2 size={52} className="mx-auto text-amber-400 mb-5" />
            <h1 className="font-['Outfit'] font-black text-3xl tracking-tight mb-3">Paiement en cours de traitement</h1>
            <p className="text-zinc-400 mb-6">
              Votre paiement est en cours de confirmation. Vous recevrez un email dès que votre compte sera prêt.
            </p>
            <button onClick={() => navigate("/")} className="text-indigo-300 font-semibold hover:underline">
              Retour à l'accueil
            </button>
          </>
        )}
        {status === "error" && (
          <>
            <h1 className="font-['Outfit'] font-black text-3xl tracking-tight mb-3">Session introuvable</h1>
            <p className="text-zinc-400 mb-6">Nous n'avons pas pu retrouver votre session de paiement.</p>
            <button onClick={() => navigate("/#tarifs")} className="text-indigo-300 font-semibold hover:underline">
              Retour aux offres
            </button>
          </>
        )}
      </div>
    </div>
  );
}
